import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { convexApiFetch } from "@/lib/apiConfig";

const SESSION_TOKEN_KEY = "c829_session_token";
const REFRESH_TOKEN_KEY = "c829_refresh_token";

function getStoredSession() {
    try { return sessionStorage.getItem(SESSION_TOKEN_KEY) || null; } catch { return null; }
}
function getStoredRefresh() {
    try { return sessionStorage.getItem(REFRESH_TOKEN_KEY) || null; } catch { return null; }
}
function storeTokens(sessionToken, refreshToken) {
    try {
        if (sessionToken) sessionStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
        if (refreshToken) sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } catch {}
}
function clearTokens() {
    try {
        sessionStorage.removeItem(SESSION_TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {}
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const refreshInFlight = useRef(false);

    // Authenticated fetch wrapper — auto-attaches Bearer token, transparently
    // refreshes on 401. Does NOT call setUser(null) on failure — callers handle it.
    const authFetch = useCallback(async (path, options = {}) => {
        const token = getStoredSession();
        const headers = {
            ...(options.headers || {}),
        };
        if (token) headers["Authorization"] = `Bearer ${token}`;

        let res = await convexApiFetch(path, { ...options, headers });

        if (res.status === 401 && getStoredRefresh()) {
            const refreshRes = await convexApiFetch("/api/auth_system/refresh_session", {
                method: "POST",
                body: JSON.stringify({ refresh_token: getStoredRefresh() }),
            });
            if (refreshRes.ok) {
                const data = await refreshRes.json();
                storeTokens(data.session_token, data.refresh_token);
                headers["Authorization"] = `Bearer ${data.session_token}`;
                res = await convexApiFetch(path, { ...options, headers });
            } else {
                clearTokens();
            }
        }
        return res;
    }, []);

    // Fetch the current user
    const refresh = useCallback(async () => {
        if (refreshInFlight.current) return;
        refreshInFlight.current = true;

        const hadTokenOnStart = !!getStoredSession();

        try {
            const res = await authFetch("/api/auth_system/me", { method: "GET" });
            if (res.ok) {
                const data = await res.json();
                // The /me endpoint returns { user: null } with HTTP 200 when
                // there is no valid session. Only set user when the value is
                // non-null; otherwise a slow anonymous probe could overwrite
                // a user set by a concurrent login().
                if (data.user) {
                    setUser(data.user);
                    if (data.session_token) {
                        storeTokens(data.session_token, getStoredRefresh());
                    }
                } else if (hadTokenOnStart) {
                    // Had a session token when the request started, but the
                    // API reports no user → session expired.
                    clearTokens();
                    setUser(null);
                }
                // else: anonymous probe (no token on start, user is null).
                // Do nothing — a concurrent login() may have set the user.
            } else if (hadTokenOnStart) {
                clearTokens();
                setUser(null);
            }
        } catch (e) {
            console.error("[Auth] refresh failed:", e);
            if (hadTokenOnStart) {
                clearTokens();
                setUser(null);
            }
        } finally {
            setIsLoading(false);
            refreshInFlight.current = false;
        }
    }, [authFetch]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        setError(null);
        const res = await convexApiFetch("/api/auth_system/login", {
            method: "POST",
            body: JSON.stringify({ email, password }),
        });
        const data = await res.json();
        if (!res.ok) {
            setError(data.error || "Login failed");
            throw new Error(data.error || "Login failed");
        }
        // Invalidate any stale in-flight refresh — the login response is
        // the authoritative source for the current session.
        refreshInFlight.current = false;
        if (data.session_token) {
            storeTokens(data.session_token, data.refresh_token);
        }
        if (data.user) {
            setUser(data.user);
        }
        setIsLoading(false);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try {
            await authFetch("/api/auth_system/logout", { method: "POST" });
        } catch (e) {
            console.error("[Auth] logout failed:", e);
        }
        clearTokens();
        setUser(null);
    }, [authFetch]);

    const register = useCallback(async (email, password, fullName) => {
        setError(null);
        const res = await convexApiFetch("/api/auth_system/register_first_superadmin", {
            method: "POST",
            body: JSON.stringify({ email, password, full_name: fullName }),
        });
        const data = await res.json();
        if (!res.ok) {
            setError(data.error || "Registration failed");
            throw new Error(data.error || "Registration failed");
        }
        refreshInFlight.current = false;
        if (data.session_token) {
            storeTokens(data.session_token, data.refresh_token);
        }
        if (data.user) {
            setUser(data.user);
        }
        setIsLoading(false);
        return data.user;
    }, []);

    const changePassword = useCallback(async (currentPassword, newPassword) => {
        const res = await authFetch("/api/auth_system/change_password", {
            method: "POST",
            body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
        });
        const data = await res.json();
        if (!res.ok) {
            throw new Error(data.error || "Failed to change password");
        }
        if (data.user) {
            setUser(data.user);
        } else {
            setUser(prev => prev ? { ...prev, must_change_password: false } : prev);
        }
        return true;
    }, [authFetch]);

    const updateProfile = useCallback(async (updates) => {
        const res = await authFetch("/api/auth_system/update_profile", {
            method: "POST",
            body: JSON.stringify(updates),
        });
        const data = await res.json();
        if (!res.ok) {
            throw new Error(data.error || "Failed to update profile");
        }
        if (data.user) {
            setUser(data.user);
        }
        return true;
    }, [authFetch]);

    const requestPasswordReset = useCallback(async (email) => {
        const res = await convexApiFetch("/api/auth_system/forgot_password", {
            method: "POST",
            body: JSON.stringify({ email }),
        });
        return res.ok;
    }, []);

    const confirmPasswordReset = useCallback(async (token, newPassword) => {
        const res = await convexApiFetch("/api/auth_system/reset_password", {
            method: "POST",
            body: JSON.stringify({ token, new_password: newPassword }),
        });
        const data = await res.json();
        if (!res.ok) {
            throw new Error(data.error || "Failed to reset password");
        }
        return true;
    }, []);

    const acceptInvite = useCallback(async (token, newPassword) => {
        const res = await convexApiFetch("/api/auth_system/accept_invite", {
            method: "POST",
            body: JSON.stringify({ token, new_password: newPassword }),
        });
        const data = await res.json();
        if (!res.ok) {
            throw new Error(data.error || "Failed to accept invitation");
        }
        if (data.session_token) {
            storeTokens(data.session_token, data.refresh_token);
        }
        if (data.user) {
            setUser(data.user);
        }
        setIsLoading(false);
        return data;
    }, []);

    const validateInviteToken = useCallback(async (token) => {
        const res = await convexApiFetch(`/api/auth_system/validate_invite?token=${encodeURIComponent(token)}`, { method: "GET" });
        return await res.json();
    }, []);

    const createWorker = useCallback(async (workerData) => {
        const res = await authFetch("/api/auth_system/create_worker", {
            method: "POST",
            body: JSON.stringify(workerData),
        });
        const data = await res.json();
        if (!res.ok) {
            throw new Error(data.error || "Failed to create worker");
        }
        return data.worker;
    }, [authFetch]);

    const value = useMemo(() => ({
        user,
        isAuthenticated: !!user,
        isLoading,
        mustChangePassword: !!user?.must_change_password,
        isAdmin: !!user?.is_platform_admin,
        isPlatformAdmin: !!user?.is_platform_admin,
        error,
        clearError: () => setError(null),
        login,
        logout,
        register,
        changePassword,
        updateProfile,
        requestPasswordReset,
        confirmPasswordReset,
        acceptInvite,
        validateInviteToken,
        createWorker,
        refresh,
        authFetch,
    }), [user, isLoading, error, login, logout, register, changePassword, updateProfile, requestPasswordReset, confirmPasswordReset, acceptInvite, validateInviteToken, createWorker, refresh, authFetch]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return ctx;
}

export function useCurrentUser() {
    const { user, isAuthenticated, isLoading } = useAuth();
    return { user, isAuthenticated, isLoading };
}